import { useState } from "react";
import { Link } from "react-router-dom";
import { Beaker, Triangle, ShoppingBag, FileText } from "lucide-react";

/**
 * Amber-image bake-off. Renders 3 Nano Banana renders as full cloaked cards
 * so we can pick before wiring into real Catalog.jsx.
 * Route: /admin/mockups/amber-picker
 */

const OPTIONS = [
  { key: "D", label: "D · Silk + Light (abstract)",    url: "/mocks/amber/amber_v2_silk_light.png" },
  { key: "E", label: "E · Wash Gradient (painterly)",  url: "/mocks/amber/amber_v2_wash_gradient.png" },
  { key: "F", label: "F · Aged Paper Grain",           url: "/mocks/amber/amber_v2_paper_grain.png" },
];

const SAMPLE_PRODUCTS = [
  { sku: "BBK-TB500-5MG",  name: "TB-500",  subtitle: "Thymosin β-4 Fragment",     aminoCount: 7,  sequencePrefix: "Ac-LKKTETQ" },
  { sku: "BBK-BPC157-5MG", name: "BPC-157", subtitle: "Body Protection Compound",  aminoCount: 15, sequencePrefix: "GEPPPGKPA" },
  { sku: "BBK-GHK-CU-50MG",name: "GHK-Cu",  subtitle: "Copper Tripeptide",         aminoCount: 3,  sequencePrefix: "Gly-His-Lys" },
  { sku: "BBK-SEMA-5MG",   name: "Semax",   subtitle: "Heptapeptide",              aminoCount: 7,  sequencePrefix: "MEHFPGP" },
];

const SUPPLY = { text: "#4A0E17", accent: "#C9A24B", ctaBg: "#4A0E17", ctaHover: "#C9A24B" };

export default function SupplyMockupAmberPicker() {
  return (
    <div className="min-h-screen bg-bone px-6 py-14 lg:py-20" data-testid="amber-picker-page">
      <div className="max-w-[1400px] mx-auto">
        <Link to="/admin/mockups/cloaked-card" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-stone hover:text-charcoal mb-8">
          ← Back to cloaked card mock
        </Link>
        <p className="overline text-oxblood mb-3">Amber Apothecary · bake-off</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-4">Pick a background.</h1>
        <p className="text-[15px] text-charcoal/80 leading-relaxed max-w-2xl mb-16">
          Three renders, all Supply-tier cloaked cards. Same peptide, same text
          layer, three different backgrounds. Warm/honey-lit target — nothing
          should read dark or heavy.
        </p>

        {OPTIONS.map(opt => (
          <section key={opt.key} className="mb-24">
            <p className="overline text-oxblood mb-3">{opt.label}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              {SAMPLE_PRODUCTS.map((p, i) => (
                <HoverCard key={`${opt.key}-${p.sku}`} product={p} bg={opt.url} testid={`amber-${opt.key}-${p.sku}`} />
              ))}
            </div>
            <div className="border border-borderSubtle bg-white/60 rounded-2xl p-6 max-w-md">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/60 mb-2">Full bg reference</p>
              <img src={opt.url} alt={opt.label} className="w-full rounded-xl" />
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function HoverCard({ product, bg, testid }) {
  const [reveal, setReveal] = useState(false);
  const t = SUPPLY;
  const { sku, name, subtitle, aminoCount, sequencePrefix } = product;
  return (
    <div
      className="relative aspect-square rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(74,14,23,0.35)] cursor-pointer group transition-[border-color,box-shadow] duration-300"
      onMouseEnter={() => setReveal(true)}
      onMouseLeave={() => setReveal(false)}
      data-testid={testid}
      style={{ borderWidth: 1, borderStyle: "solid", borderColor: reveal ? t.text : `${t.text}33` }}
    >
      <img src={bg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none" draggable={false} />
      <div
        className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
        style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 55%, rgba(255,255,255,0) 100%)", opacity: reveal ? 1 : 0.5 }}
      />
      <div className="absolute top-3 left-3 z-10">
        <p className="font-serif text-[12px] font-semibold leading-none tracking-tight" style={{ color: t.text }}>
          BulkBuyKits<span className="font-normal">.com</span>
        </p>
        <div className="mt-1.5 h-[2px] w-8 rounded-full" style={{ background: t.accent }} />
      </div>
      <div className="absolute top-3 right-3 z-10">
        <span className="inline-flex items-center rounded-full bg-white/75 backdrop-blur-sm font-mono uppercase tracking-[0.24em] shadow-sm text-[7.5px] px-1.5 py-[3px]" style={{ color: t.text }}>RUO</span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 px-6" style={{ opacity: reveal ? 0 : 1 }}>
        <div className="text-center max-w-[75%]">
          <p className="font-serif text-[22px] leading-[1.05] tracking-tight select-none" style={{ color: t.text, opacity: 0.72 }}>Research</p>
          <p className="font-serif text-[22px] leading-[1.05] tracking-tight select-none" style={{ color: t.text, opacity: 0.72 }}>Peptide</p>
          <p className="font-mono text-[7.5px] mt-3 uppercase tracking-[0.28em]" style={{ color: t.text, opacity: 0.85 }}>Hover to reveal</p>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center pointer-events-none transition-opacity duration-300" style={{ opacity: reveal ? 1 : 0 }}>
        <div className="pl-3 pr-3 max-w-[68%]">
          <h3 className="font-serif font-bold text-[26px] leading-none tracking-tight drop-shadow-sm mb-1.5" style={{ color: t.text }}>{name}</h3>
          <p className="font-serif italic text-[10.5px] leading-tight mb-3" style={{ color: t.text, opacity: 0.7 }}>{subtitle}</p>
          <div className="space-y-1.5 text-[9.5px]" style={{ color: t.text }}>
            <div className="flex items-center gap-2"><Triangle size={10} strokeWidth={1.8} style={{ color: t.text }} /><span>{aminoCount} Amino Acids</span></div>
            <div className="flex items-center gap-2"><Beaker size={10} strokeWidth={1.8} style={{ color: t.text }} /><span className="font-mono text-[9px]">{sequencePrefix}</span></div>
          </div>
        </div>
      </div>
      <div className="absolute left-3 right-3 bottom-3 z-10 flex items-center gap-1.5 transition-all duration-300" style={{ opacity: reveal ? 1 : 0, pointerEvents: reveal ? "auto" : "none", transform: reveal ? "translateY(0)" : "translateY(8px)" }}>
        <button className="flex-1 inline-flex items-center justify-center rounded-full text-white font-mono uppercase tracking-[0.22em] font-semibold shadow-lg text-[8.5px] px-2 py-1.5 gap-1" style={{ background: t.ctaBg }}>
          <ShoppingBag size={10} strokeWidth={2.5} />Add to Cart
        </button>
        <button className="inline-flex items-center justify-center rounded-full bg-white/85 backdrop-blur-sm font-mono uppercase tracking-[0.22em] font-semibold shadow-md text-[8.5px] px-2 py-1.5 gap-1" style={{ color: t.text, borderColor: `${t.text}4D`, borderWidth: 1 }}>
          <FileText size={10} strokeWidth={2.5} />COA
        </button>
      </div>
    </div>
  );
}
